import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

// Helper function for QA badge color
function getQABadgeColor(score) {
  if (score >= 85) return '#22c55e'; // green
  if (score >= 70) return '#f59e0b'; // orange
  return '#ef4444'; // red
}

// Translation Display Component - OUTSIDE main function to prevent re-creation
function TranslationDisplay({ translation, englishText, onEdit, t }) {
  const hasTranslation = translation && translation.translated_text;
  const qaScore = translation?.qa_score;
  const needsReview = qaScore && qaScore < 85;

  return (
    <div className="translation-section" style={{ marginTop: '12px', padding: '12px', background: 'var(--background)', borderRadius: '8px', border: '1px solid var(--border)' }}>
      {/* English */}
      <div style={{ marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginRight: '8px' }}>[EN]</span>
        <span style={{ fontSize: '13px' }}>{englishText || '—'}</span>
      </div>
      {/* Thai translation */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginRight: '8px' }}>[TH]</span>
          <span style={{ fontSize: '13px' }}>{hasTranslation ? translation.translated_text : '—'}</span>
          {hasTranslation && qaScore && (
            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: getQABadgeColor(qaScore), fontWeight: '500' }}>
                {t('translation.aiConfidence', { score: qaScore })}{qaScore >= 85 ? ' ' + t('translation.verified') : ''}
              </span>
              {translation.status === 'owner_edited' && (
                <span style={{ fontSize: '10px', background: '#dbeafe', color: '#1d4ed8', padding: '2px 6px', borderRadius: '4px' }}>{t('translation.ownerEdited')}</span>
              )}
            </div>
          )}
          {needsReview && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#f59e0b' }}>
              {t('translation.needsReview')}
            </div>
          )}
        </div>
        {hasTranslation && (
          <button
            onClick={() => onEdit(translation)}
            className="btn btn-secondary"
            style={{ padding: '4px 8px', fontSize: '11px' }}
          >
            {t('common.edit')}
          </button>
        )}
      </div>
      {!hasTranslation && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          {t('translation.noTranslation')}
        </div>
      )}
    </div>
  );
}

// Field with Default Toggle - OUTSIDE main function to prevent re-creation
function FieldWithDefault({
  t,
  field,
  label,
  rows = 3,
  info,
  onInfoChange,
  useDefaults,
  onToggleDefault,
  ownerSettings,
  translation,
  onEditTranslation
}) {
  const isUsingDefault = useDefaults[field];
  const hasDefault = ownerSettings && ownerSettings['default_' + field];
  const defaultValue = hasDefault ? ownerSettings['default_' + field] : '';
  const currentValue = info[field] || '';

  const handleChange = useCallback((e) => {
    onInfoChange(field, e.target.value);
  }, [field, onInfoChange]);

  return (
    <div className="form-group" style={{ marginBottom: '24px' }}>
      {/* Label on its own line */}
      <label className="label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>{label}</label>

      {/* Toggle row (if has default) */}
      {hasDefault && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {isUsingDefault ? 'Using default' : 'Custom'}
          </span>
          <button
            type="button"
            onClick={() => onToggleDefault(field)}
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: isUsingDefault ? 'var(--primary)' : 'var(--border)',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.2s'
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '2px',
                left: isUsingDefault ? '22px' : '2px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'white',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          </button>
          {isUsingDefault && (
            <Link to="/settings" style={{ fontSize: '12px', color: 'var(--primary)', marginLeft: '8px' }}>
              Edit in Settings
            </Link>
          )}
        </div>
      )}

      {/* Show default value (read-only) if using default */}
      {isUsingDefault && hasDefault && (
        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '6px', fontSize: '13px', color: 'var(--text)', whiteSpace: 'pre-wrap', border: '1px solid var(--border)' }}>
          {defaultValue}
        </div>
      )}

      {/* Editable textarea if custom */}
      {!isUsingDefault && (
        <textarea
          className="input"
          rows={rows}
          value={currentValue}
          onChange={handleChange}
          style={{ width: '100%' }}
        />
      )}

      {/* Translation display */}
      {(currentValue || defaultValue) && (
        <TranslationDisplay
          translation={translation}
          englishText={isUsingDefault ? defaultValue : currentValue}
          onEdit={onEditTranslation}
          t={t}
        />
      )}
    </div>
  );
}

// Main Component
function OwnerProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === 'admin';

  const [property, setProperty] = useState(null);
  const [offers, setOffers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // Form state
  const [branding, setBranding] = useState({});
  const [info, setInfo] = useState({});

  // Owner settings (defaults)
  const [ownerSettings, setOwnerSettings] = useState(null);
  const [useDefaults, setUseDefaults] = useState({
    house_rules: true,
    checkin_info: true,
    checkout_info: true,
    emergency_info: true
  });

  // Translations with QA scores
  const [translations, setTranslations] = useState([]);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [editText, setEditText] = useState('');
  const [editReason, setEditReason] = useState('');
  const [savingTranslation, setSavingTranslation] = useState(false);

  useEffect(() => {
    if (user) {
      loadProperty();
    }

    async function loadProperty() {
      try {
        const endpoint = isAdmin ? `/admin/properties/${id}` : `/owner/properties/${id}`;
        const [propData, offersData, analyticsData, settingsData, translationsData] = await Promise.all([
          api.get(endpoint),
          api.get(`/owner/properties/${id}/offers`),
          api.get(`/owner/properties/${id}/analytics`),
          api.get('/owner/settings').catch(() => ({ settings: null })),
          api.get(`/owner/properties/${id}/translations`).catch(() => [])
        ]);
        setProperty(propData);
        setBranding(propData.branding || {});
        setInfo(propData.info || {});
        setOffers(offersData);
        setAnalytics(analyticsData);
        setOwnerSettings(settingsData.settings);
        setTranslations(translationsData);

        // Set useDefaults from property info if available
        if (propData.info?.use_owner_defaults) {
          setUseDefaults(propData.info.use_owner_defaults);
        }
      } catch (err) {
        console.error('Failed to load property:', err);
        navigate('/properties');
      } finally {
        setLoading(false);
      }
    }
  }, [user, id, isAdmin, navigate]);

  // Memoized handlers to prevent re-renders
  const handleInfoChange = useCallback((field, value) => {
    setInfo(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleUseDefault = useCallback((field) => {
    setUseDefaults(prev => {
      const newDefaults = { ...prev, [field]: !prev[field] };
      // If switching to default, copy the default value
      if (!prev[field] && ownerSettings) {
        const defaultKey = 'default_' + field;
        if (ownerSettings[defaultKey]) {
          setInfo(prevInfo => ({ ...prevInfo, [field]: ownerSettings[defaultKey] }));
        }
      }
      return newDefaults;
    });
  }, [ownerSettings]);

  const handleEditTranslation = useCallback((translation) => {
    setEditingTranslation(translation);
    setEditText(translation.translated_text);
  }, []);

  // Get translation by field name (with property_ prefix)
  const getTranslation = useCallback((field) => {
    const entityType = 'property_' + field;
    return translations.find(tr => tr.entity_type === entityType);
  }, [translations]);

  // Check if property info is complete
  const isPropertyComplete = useCallback(() => {
    const hasWifi = info.wifi_ssid;
    const hasHouseRules = useDefaults.house_rules ? ownerSettings?.default_house_rules : info.house_rules;
    const hasCheckin = useDefaults.checkin_info ? ownerSettings?.default_checkin_info : info.checkin_info;
    const hasCheckout = useDefaults.checkout_info ? ownerSettings?.default_checkout_info : info.checkout_info;
    return hasWifi && hasHouseRules && hasCheckin && hasCheckout;
  }, [info, useDefaults, ownerSettings]);

  async function handleSaveBranding() {
    setSaving(true);
    try {
      await api.put(`/owner/properties/${id}/branding`, branding);
      alert(t('owner.brandingSaved'));
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveInfo() {
    setSaving(true);
    try {
      await api.put(`/owner/properties/${id}/info`, { ...info, use_owner_defaults: useDefaults });
      alert(t('owner.infoSaved'));
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    } finally {
      setSaving(false);
    }
  }

  async function handleUploadImage(e, field) {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await api.upload('/upload', file);
      setBranding(prev => ({ ...prev, [field]: result.url }));
    } catch (err) {
      alert(t('common.uploadFailed') + ': ' + err.message);
    }
  }

  async function handleDownloadQR(format = 'png') {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/owner/properties/${id}/qr?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to download QR code');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${property.unit_code}-qr.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    }
  }

  async function handleSaveTranslation() {
    if (!editingTranslation) return;
    setSavingTranslation(true);
    try {
      await api.put(`/owner/properties/${id}/translations/${editingTranslation.id}`, {
        translated_text: editText,
        edit_reason: editReason
      });
      // Update local state
      setTranslations(prev => prev.map(tr =>
        tr.id === editingTranslation.id
          ? { ...tr, translated_text: editText, status: 'owner_edited' }
          : tr
      ));
      setEditingTranslation(null);
      setEditText('');
      setEditReason('');
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    } finally {
      setSavingTranslation(false);
    }
  }

  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  if (!property) return null;

  const propertyComplete = isPropertyComplete();

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 style={{ fontSize: '20px', fontWeight: '600' }}>{property.name}</h1>
        <span className="text-secondary">{property.unit_code}</span>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          {t('owner.propertyInfo')}
        </button>
        <button className={`tab ${activeTab === 'branding' ? 'active' : ''}`} onClick={() => setActiveTab('branding')}>
          {t('owner.branding')}
        </button>
        <button className={`tab ${activeTab === 'offers' ? 'active' : ''}`} onClick={() => setActiveTab('offers')}>
          {t('owner.assignedOffers')}
        </button>
        <button className={`tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
          {t('owner.analytics')}
        </button>
      </div>

      {/* Info Tab */}
      {activeTab === 'info' && (
        <div className="card">
          {/* QR Code Section - TOP */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{t('owner.downloadQR')}</h3>
            {!propertyComplete && (
              <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: '#92400e' }}>
                ⚠️ Property info is incomplete. Some fields are missing. You can still generate a QR code, but guests may see incomplete information.
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => handleDownloadQR('png')} className="btn btn-secondary" style={{ flex: 1 }}>
                {propertyComplete ? t('owner.downloadPNG') : 'Download PNG Anyway'}
              </button>
              <button onClick={() => handleDownloadQR('svg')} className="btn btn-secondary" style={{ flex: 1 }}>
                {propertyComplete ? t('owner.downloadSVG') : 'Download SVG Anyway'}
              </button>
            </div>
          </div>

          {/* WiFi fields (no translation needed) */}
          <div className="form-group">
            <label className="label" style={{ display: 'block', marginBottom: '8px' }}>{t('guest.wifiName')}</label>
            <input
              type="text"
              className="input"
              value={info.wifi_ssid || ''}
              onChange={(e) => handleInfoChange('wifi_ssid', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="label" style={{ display: 'block', marginBottom: '8px' }}>{t('guest.wifiPassword')}</label>
            <input
              type="text"
              className="input"
              value={info.wifi_password || ''}
              onChange={(e) => handleInfoChange('wifi_password', e.target.value)}
            />
          </div>

          {/* Divider */}
          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

          {/* Fields with default toggle and translation display */}
          <FieldWithDefault
            t={t}
            field="house_rules"
            label={t('guest.houseRules')}
            rows={3}
            info={info}
            onInfoChange={handleInfoChange}
            useDefaults={useDefaults}
            onToggleDefault={toggleUseDefault}
            ownerSettings={ownerSettings}
            translation={getTranslation('house_rules')}
            onEditTranslation={handleEditTranslation}
          />
          <FieldWithDefault
            t={t}
            field="checkin_info"
            label={t('guest.checkIn')}
            rows={2}
            info={info}
            onInfoChange={handleInfoChange}
            useDefaults={useDefaults}
            onToggleDefault={toggleUseDefault}
            ownerSettings={ownerSettings}
            translation={getTranslation('checkin_info')}
            onEditTranslation={handleEditTranslation}
          />
          <FieldWithDefault
            t={t}
            field="checkout_info"
            label={t('guest.checkOut')}
            rows={2}
            info={info}
            onInfoChange={handleInfoChange}
            useDefaults={useDefaults}
            onToggleDefault={toggleUseDefault}
            ownerSettings={ownerSettings}
            translation={getTranslation('checkout_info')}
            onEditTranslation={handleEditTranslation}
          />
          <FieldWithDefault
            t={t}
            field="emergency_info"
            label={t('guest.emergency')}
            rows={2}
            info={info}
            onInfoChange={handleInfoChange}
            useDefaults={useDefaults}
            onToggleDefault={toggleUseDefault}
            ownerSettings={ownerSettings}
            translation={getTranslation('emergency_info')}
            onEditTranslation={handleEditTranslation}
          />

          <button onClick={handleSaveInfo} className="btn btn-primary btn-block" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className="card">
          <div className="form-group">
            <label className="label">{t('owner.logo')}</label>
            <p className="text-secondary text-xs mb-2">{t('owner.logoHelp')}</p>
            {branding.logo_url && (
              <img src={branding.logo_url} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }} />
            )}
            <input type="file" accept="image/*" onChange={(e) => handleUploadImage(e, 'logo_url')} />
            <div className="mt-3">
              <p className="text-xs text-secondary mb-2">{t('owner.chooseTemplate')}</p>
              <div className="flex gap-3 flex-wrap">
                {['beach', 'city', 'mountain', 'tropical'].map(style => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => setBranding(prev => ({ ...prev, logo_url: `/templates/logo-${style}.svg` }))}
                    style={{
                      padding: 0,
                      border: branding.logo_url === `/templates/logo-${style}.svg` ? '3px solid var(--primary)' : '2px solid var(--border)',
                      borderRadius: '8px',
                      background: 'none',
                      cursor: 'pointer',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={`/templates/logo-${style}.svg`}
                      alt={style}
                      style={{ width: '60px', height: '60px', display: 'block' }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="label">{t('owner.coverImage')}</label>
            <p className="text-secondary text-xs mb-2">{t('owner.coverHelp')}</p>
            {branding.cover_image_url && (
              <img src={branding.cover_image_url} alt="" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }} />
            )}
            <input type="file" accept="image/*" onChange={(e) => handleUploadImage(e, 'cover_image_url')} />
            <div className="mt-3">
              <p className="text-xs text-secondary mb-2">{t('owner.chooseTemplate')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {['beach', 'city', 'mountain', 'tropical'].map(style => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => setBranding(prev => ({ ...prev, cover_image_url: `/templates/cover-${style}.svg` }))}
                    style={{
                      padding: 0,
                      border: branding.cover_image_url === `/templates/cover-${style}.svg` ? '3px solid var(--primary)' : '2px solid var(--border)',
                      borderRadius: '8px',
                      background: 'none',
                      cursor: 'pointer',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={`/templates/cover-${style}.svg`}
                      alt={style}
                      style={{ width: '100%', height: '60px', objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="label">{t('owner.accentColor')}</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={branding.accent_color || '#2563eb'}
                onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                style={{ width: '50px', height: '36px', padding: '2px', border: '1px solid var(--border)', borderRadius: '4px' }}
              />
              <input
                type="text"
                className="input"
                value={branding.accent_color || '#2563eb'}
                onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                style={{ flex: 1 }}
                placeholder="#2563eb"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="label">{t('owner.tagline')}</label>
            <input
              type="text"
              className="input"
              value={branding.tagline || ''}
              onChange={(e) => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
              placeholder={t('owner.taglinePlaceholder')}
            />
          </div>
          <button onClick={handleSaveBranding} className="btn btn-primary btn-block" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      )}

      {/* Offers Tab */}
      {activeTab === 'offers' && (
        <div className="card">
          <p className="text-secondary text-sm mb-3">{t('owner.offersNote')}</p>
          {offers.length === 0 ? (
            <p className="text-secondary">{t('owner.noOffersAssigned')}</p>
          ) : (
            offers.map(offer => (
              <div key={offer.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div className="flex justify-between items-center">
                  <div>
                    <h4 style={{ fontSize: '14px' }}>{offer.title}</h4>
                    <p className="text-secondary text-xs">{offer.vendor_name} • {offer.category_name}</p>
                  </div>
                  <div className="text-secondary text-xs text-right">
                    {offer.walk_time_min && <div>🚶 {offer.walk_time_min} min</div>}
                    {offer.drive_time_min && <div>🚗 {offer.drive_time_min} min</div>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div>
          <div className="flex gap-3 mb-4">
            <div className="card" style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary)' }}>
                {analytics.totals.scans}
              </div>
              <div className="text-secondary text-xs">{t('owner.totalScans')}</div>
            </div>
            <div className="card" style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {analytics.totals.clicks}
              </div>
              <div className="text-secondary text-xs">{t('owner.totalClicks')}</div>
            </div>
            <div className="card" style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--success)' }}>
                {analytics.totals.redemptions}
              </div>
              <div className="text-secondary text-xs">{t('owner.totalRedemptions')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Translation Edit Modal */}
      {editingTranslation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setEditingTranslation(null); }}
        >
          <div
            className="card"
            style={{ width: '100%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto' }}
          >
            <h3 style={{ marginBottom: '16px' }}>Edit Translation</h3>
            <div className="form-group">
              <label className="label">Original (English)</label>
              <div style={{ padding: '8px', background: '#f8fafc', borderRadius: '6px', fontSize: '13px' }}>
                {editingTranslation.source_text || '—'}
              </div>
            </div>
            <div className="form-group">
              <label className="label">Translation (Thai)</label>
              <textarea
                className="input"
                rows={4}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="label">Reason for edit (optional)</label>
              <input
                type="text"
                className="input"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="e.g., Fixed grammar, More natural phrasing"
              />
            </div>
            <div style={{ padding: '8px', background: '#fef3c7', borderRadius: '6px', marginBottom: '16px', fontSize: '12px' }}>
              ⚠️ Your edit will be applied immediately. Admin will be notified for review.
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditingTranslation(null)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTranslation}
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={savingTranslation}
              >
                {savingTranslation ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OwnerProperty;
